import { type B2bConfig, type B2bCustomer, createB2bScheme, listB2bProducts, publishB2bScheme, type SchemeDraftInput, updateB2bScheme } from '@api/b2b';
import { ActionIcon, Button, Checkbox, Modal, MultiSelect, NumberInput, Select, TagsInput, TextInput } from '@mantine/core';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import styles from './b2b.module.css';
import { pickDraft, type SchemeEditing } from './schemeDrafts';
import { describeScheme, errorMessage } from './utils';


const BENEFITS = [
    { value: 'FREE_QTY', label: 'Free goods (buy N get M)' },
    { value: 'PERCENT', label: 'Percentage off' },
    { value: 'AMOUNT', label: 'Amount off' },
];
const EXCLUSIVITY = [
    { value: 'NONE', label: 'Give all the schemes it qualifies for' },
    { value: 'CLASS', label: 'Give only one free-goods / one % off / one ₹ off' },
    { value: 'GLOBAL', label: 'Give only this one' },
];
const EXCLUSIVITY_HINT: Record<string, string> = {
    NONE: 'This scheme always applies, together with every other scheme the order qualifies for.',
    CLASS: 'The order gets at most one free-goods scheme, one % off and one ₹ off. If it qualifies for two of the same kind, only the one with the higher priority applies.',
    GLOBAL: 'If this has the highest priority of the schemes the order qualifies for, no other scheme applies. If another has a higher priority, this one is dropped.',
};
/** Hints sit under the box, so boxes in a row line up whatever the hint. */
const BELOW: Array<'label' | 'input' | 'description' | 'error'> = ['label', 'input', 'description', 'error'];

/**
 * The scheme form, as a pop-up wherever schemes are managed (the Pricing page, a customer's
 * Schemes tab). Saves a draft, or saves and publishes it for an admin.
 */
const SchemeEditor: React.FC<{
    editing: SchemeEditing;
    config: B2bConfig | undefined;
    customers: B2bCustomer[];
    isAdmin: boolean;
    onClose: () => void;
}> = ({ editing, config, customers, isAdmin, onClose }) => {
    const queryClient = useQueryClient();
    const { data: catalogue = [] } = useQuery({ queryKey: ['b2b-products'], queryFn: listB2bProducts, staleTime: 5 * 60 * 1000 });
    const categories = [...new Set(catalogue.map((p) => p.category).filter((c): c is string => !!c))];
    const products = catalogue.map((p) => ({ value: p.productId, label: `${p.name} (${p.sku})` }));
    const [d, setD] = useState<SchemeDraftInput>(editing.draft);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = (patch: Partial<SchemeDraftInput>) => setD((x) => ({ ...x, ...patch }));
    const setRule = (patch: Partial<SchemeDraftInput['rule']>) => setD((x) => ({ ...x, rule: { ...x.rule, ...patch } }));
    const slabs = d.rule.slabs;
    const setSlab = (i: number, patch: Partial<(typeof slabs)[number]>) => setRule({ slabs: slabs.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
    const free = d.benefitType === 'FREE_QTY';
    const unit = d.slabBasis === 'VALUE' ? '₹' : 'units';

    const save = async (publish: boolean) => {
        setBusy(true);
        setError(null);
        try {
            const body = pickDraft(d);
            const saved = editing.id ? await updateB2bScheme(editing.id, body) : await createB2bScheme(body);
            if (publish) await publishB2bScheme(saved.id);
            await Promise.all([queryClient.invalidateQueries({ queryKey: ['b2b-schemes'] }), queryClient.invalidateQueries({ queryKey: ['b2b-catalog'] })]);
            onClose();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal opened onClose={onClose} title={editing.draft.code ? `Edit ${editing.draft.code}` : 'New scheme'} centered size="xl">
            <div className={styles.stack}>
                <div className={styles.fieldGrid}>
                    <TextInput
                        label="Scheme code"
                        description="Stays the same across versions"
                        inputWrapperOrder={BELOW}
                        required
                        value={d.code}
                        disabled={!!editing.draft.code}
                        onChange={(e) => set({ code: e.currentTarget.value.toUpperCase() })}
                    />
                    <TextInput label="Name" required value={d.name} onChange={(e) => set({ name: e.currentTarget.value })} />
                    <TextInput label="From" type="date" value={d.effectiveFrom} onChange={(e) => set({ effectiveFrom: e.currentTarget.value })} />
                    <TextInput label="To (optional)" type="date" value={d.effectiveTo ?? ''} onChange={(e) => set({ effectiveTo: e.currentTarget.value || undefined })} />
                    <Select label="Benefit" data={BENEFITS} value={d.benefitType} onChange={(v) => v && set({ benefitType: v as SchemeDraftInput['benefitType'] })} />
                    <Select
                        label="Count the threshold in"
                        description="Added up across all the lines that count, not per product"
                        inputWrapperOrder={BELOW}
                        data={[
                            { value: 'QTY', label: 'Units ordered' },
                            { value: 'VALUE', label: 'Order value (₹, before GST)' },
                        ]}
                        value={d.slabBasis}
                        onChange={(v) => v && set({ slabBasis: v as SchemeDraftInput['slabBasis'] })}
                    />
                    <Select
                        label="If an order earns this and other schemes too"
                        description={EXCLUSIVITY_HINT[d.exclusivity]}
                        inputWrapperOrder={BELOW}
                        data={EXCLUSIVITY}
                        value={d.exclusivity}
                        onChange={(v) => v && set({ exclusivity: v as SchemeDraftInput['exclusivity'] })}
                    />
                    <NumberInput
                        label="Priority"
                        description="1 = highest priority, then 2, 3… Only used when the setting beside it makes schemes compete. Not linked to units or value."
                        inputWrapperOrder={BELOW}
                        min={1}
                        allowDecimal={false}
                        value={d.priority}
                        onChange={(v) => set({ priority: Number(v) || 0 })}
                    />
                </div>

                <span className={styles.cardTitle}>Slabs</span>
                <span className={styles.cardHint}>The highest slab the order reaches applies.</span>
                {slabs.map((slab, i) => (
                    <div key={i} className={styles.row}>
                        <NumberInput size="xs" label={`From (${unit})`} min={0} value={slab.min} onChange={(v) => setSlab(i, { min: Number(v) || 0, max: null })} w={140} />
                        {free ? (
                            <>
                                <NumberInput size="xs" label="Buy (units)" min={1} allowDecimal={false} value={slab.buy ?? ''} onChange={(v) => setSlab(i, { buy: Number(v) || undefined })} w={120} />
                                <NumberInput size="xs" label="Get free (units)" min={1} allowDecimal={false} value={slab.get ?? ''} onChange={(v) => setSlab(i, { get: Number(v) || undefined })} w={120} />
                            </>
                        ) : (
                            <NumberInput
                                size="xs"
                                label={d.benefitType === 'PERCENT' ? 'Percent off (%)' : 'Amount off (₹)'}
                                min={0}
                                value={slab.value ?? ''}
                                onChange={(v) => setSlab(i, { value: v === '' ? undefined : Number(v) })}
                                w={140}
                            />
                        )}
                        <ActionIcon mt={20} variant="subtle" color="gray" aria-label="Remove slab" onClick={() => setRule({ slabs: slabs.filter((_, j) => j !== i) })}>
                            <TrashIcon size={16} />
                        </ActionIcon>
                    </div>
                ))}
                <Button size="xs" variant={ButtonVariant.SUBTLE} leftSection={<PlusIcon size={14} />} w="fit-content" onClick={() => setRule({ slabs: [...slabs, { min: 0, max: null }] })}>
                    Add slab
                </Button>
                {slabs.length ? <span className={styles.cardHint}>Reads as: {describeScheme(d)}.</span> : null}

                <span className={styles.cardTitle}>Which lines count</span>
                <div className={styles.fieldGrid}>
                    <MultiSelect
                        label="Categories (blank = all)"
                        data={categories}
                        value={d.rule.productScope?.categories ?? []}
                        onChange={(v) => setRule({ productScope: { categories: v, productIds: d.rule.productScope?.productIds ?? [] } })}
                        searchable
                    />
                    <MultiSelect
                        label="Or specific products"
                        data={products}
                        value={d.rule.productScope?.productIds ?? []}
                        onChange={(v) => setRule({ productScope: { categories: d.rule.productScope?.categories ?? [], productIds: v } })}
                        searchable
                    />
                </div>
                {free ? (
                    <div className={styles.fieldGrid}>
                        <Select
                            label="Free pieces may be"
                            description="What the customer may pick their free units from"
                            inputWrapperOrder={BELOW}
                            data={[
                                { value: 'SAME_ITEM', label: 'Products ordered' },
                                { value: 'LIST', label: 'Products from a list' },
                                // No longer offered: the order screen lists only products on the order. Kept for a scheme already saved with it.
                                ...(d.rule.freePool?.type === 'ANY' ? [{ value: 'ANY', label: 'Any product in their catalogue' }] : []),
                            ]}
                            value={d.rule.freePool?.type ?? 'SAME_ITEM'}
                            onChange={(v) => setRule({ freePool: { type: (v as 'ANY' | 'SAME_ITEM' | 'LIST') ?? 'SAME_ITEM', productIds: d.rule.freePool?.productIds ?? [] } })}
                        />
                        {d.rule.freePool?.type === 'LIST' ? (
                            <MultiSelect label="Free products" data={products} value={d.rule.freePool.productIds} onChange={(v) => setRule({ freePool: { type: 'LIST', productIds: v } })} searchable />
                        ) : null}
                    </div>
                ) : null}

                <span className={styles.cardTitle}>Who gets it</span>
                <Checkbox label="All customers" checked={d.appliesToAll} onChange={(e) => set({ appliesToAll: e.currentTarget.checked })} />
                {!d.appliesToAll ? (
                    <div className={styles.fieldGrid}>
                        <MultiSelect label="Segments" data={(config?.segments ?? []).map((s) => ({ value: s.key, label: s.label }))} value={d.segments} onChange={(v) => set({ segments: v })} />
                        <TagsInput label="Customer tags" value={d.tags} onChange={(v) => set({ tags: v })} />
                        <MultiSelect label="Customers" data={customers.map((c) => ({ value: c.customerId, label: c.name }))} value={d.customerIds} onChange={(v) => set({ customerIds: v })} searchable />
                    </div>
                ) : null}

                {error ? <span className={styles.error}>{error}</span> : null}
                <div className={styles.row}>
                    {isAdmin ? (
                        <Button variant={ButtonVariant.ACTION} loading={busy} disabled={!d.code.trim() || !d.name.trim() || slabs.length === 0} onClick={() => void save(true)}>
                            Save and publish
                        </Button>
                    ) : null}
                    <Button variant={ButtonVariant.DEFAULT} loading={busy} disabled={!d.code.trim() || !d.name.trim() || slabs.length === 0} onClick={() => void save(false)}>
                        Save draft
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SchemeEditor;
